import {useApolloClient} from "@apollo/client";
import {OnChangeFn} from "@tanstack/react-table";
import classNames from "classnames";
import {ReactNode} from "react";
import {FiPlus} from "react-icons/fi";
import {Link} from "react-router";

import Button from "@/components/button/Button";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {GQL_MAINTENANCE_SCHEDULES_LIST, MaintenanceListResponseValues} from "@/lib/queries/maintenance.ts";
import {MaintenanceScheduleListItem} from "@/lib/types/maintenance.ts";
import {ChecksTags} from "@/pages/checks/list/components/CheckTags.tsx";
import {AffectedChecksCell} from "@/pages/maintenance/maintenance-dashboard/components/AffectedChecksCell/AffectedChecksCell.tsx";
import {MaintenanceSchedulesStatePill} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/MaintenanceSchedulesStatePill.tsx";
import {MaintenanceSchedulesTableActions} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/MaintenanceSchedulesTableActions.tsx";
import {TimeUntil} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/components/TimeUntil.tsx";
import {getDurationDisplay} from "@/pages/maintenance/maintenance-dashboard/tabs/schedules/MaintenanceSchedulesTable/MaintenanceSchedulesTable.utils.ts";
import {getRecurrenceDisplay} from "@/pages/maintenance/maintenance.utils.ts";

import styles from "./MaintenanceSchedulesTable.module.scss";

type MaintenanceSchedulesTableProps = {
  data?: MaintenanceListResponseValues;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  pagination: LimitAndOffset;
  setPagination: (pagination: LimitAndOffset) => void;
  isFiltered?: boolean;
  loading?: boolean;
  rowSelection?: Record<string, boolean>;
  setRowSelection?: OnChangeFn<Record<string, boolean>>;
  actionBar?: ReactNode;
};

export const MaintenanceSchedulesTable = ({
  data,
  sortColumn,
  sortDirection,
  onSortingChange,
  pagination,
  setPagination,
  isFiltered,
  loading,
  rowSelection,
  setRowSelection,
  actionBar,
}: MaintenanceSchedulesTableProps) => {
  const {user} = useCurrentUserContext();
  const client = useApolloClient();

  //added to only refresh the table after you are done deleting services.
  const handleChecksModalClose = () => {
    client.refetchQueries({include: [GQL_MAINTENANCE_SCHEDULES_LIST]});
  };

  const specs: ColumnSpec<MaintenanceScheduleListItem>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      enableSorting: true,
      meta: {
        headerClassName: styles.nameColumn,
      },
      accessor: (schedule: MaintenanceScheduleListItem) => (
        <Tooltip tooltipContent={schedule.name} delayMs={500}>
          <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{schedule.name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.statusColumn),
      },
      accessor: (schedule: MaintenanceScheduleListItem) => <MaintenanceSchedulesStatePill schedule={schedule} />,
    },
    {
      fieldName: "next_occurrence",
      fieldTitle: "Next Scheduled",
      enableSorting: true,
      meta: {
        headerClassName: classNames("text-nowrap", styles.nextScheduledColumn),
      },
      accessor: (schedule: MaintenanceScheduleListItem) => <TimeUntil schedule={schedule} user={user!} />,
    },
    {
      fieldName: "type",
      fieldTitle: "Repeat",
      meta: {
        headerClassName: classNames(styles.repeatColumn),
      },
      accessor: (schedule: MaintenanceScheduleListItem) => {
        const recurrenceDisplay = getRecurrenceDisplay(schedule);
        return (
          <Tooltip tooltipContent={recurrenceDisplay} delayMs={500}>
            <div className="ellipsis-overflow pulseTextBodySmall pulse-color-gray-900">{recurrenceDisplay}</div>
          </Tooltip>
        );
      },
    },
    {
      fieldName: "duration_minutes",
      fieldTitle: "Duration",
      meta: {
        headerClassName: classNames("text-nowrap", styles.durationColumn),
      },
      accessor: (schedule: MaintenanceScheduleListItem) => (
        <span className="pulseTextBodySmall">{getDurationDisplay(schedule.duration_minutes)}</span>
      ),
    },
    {
      fieldName: "services",
      fieldTitle: "Checks Used In",
      enableSorting: true,
      meta: {
        headerClassName: classNames(styles.checksColumn),
      },
      accessor: (schedule: MaintenanceScheduleListItem) => (
        <AffectedChecksCell
          services={schedule.services_preview}
          count={schedule.services_count}
          modelId={schedule.id}
          modelType={"MaintenanceSchedule"}
          title={"Schedule: " + schedule.name}
          onClose={handleChecksModalClose}
        />
      ),
    },
    {
      fieldName: "tags",
      fieldTitle: "Tags",
      meta: {
        headerClassName: styles.tagsColumn,
      },
      accessor: (schedule: MaintenanceScheduleListItem) => <ChecksTags tags={schedule.tags} />,
    },
    ...(user?.has_write_access
      ? [
          {
            fieldName: "actions",
            meta: {
              headerClassName: styles.actionColumn,
            },
            accessor: (schedule: MaintenanceScheduleListItem) => (
              <MaintenanceSchedulesTableActions schedule={schedule} />
            ),
          },
        ]
      : []),
  ];
  return (
    <>
      <Table
        rows={data?.items || []}
        specs={specs}
        initialState={{columnPinning: {right: ["actions"]}}}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        isLoading={loading}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        getRowId={(row) => String(row.id)}
        actionBar={actionBar}
        noDataComponent={
          <TableEmptyStateCta
            colSpan={specs.length}
            title={
              isFiltered
                ? "No Maintenance Schedules found matching the filter(s) above."
                : "You do not have any Maintenance Schedules."
            }
            cta={
              isFiltered ? undefined : (
                <Button as={Link} to="/maintenance/schedules/create" variant="secondary">
                  <Icon icon={FiPlus} />
                  <span>Create Schedule</span>
                </Button>
              )
            }
          />
        }
        fixedWidth={true}
        minWidth={"1326px"}
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={data?.total || 0}
        paginationData={data?.items}
      />
    </>
  );
};
